import React, { useState, useEffect } from 'react';

// MedicalAddModal component for adding or editing medical center details in a modal
const MedicalAddModal = ({
  show, // Boolean to control modal visibility
  onClose, // Function to close the modal
  formData, // Object containing form input data
  onChange, // Function to handle form input changes
  onSave, // Function to save form data
  isEditMode, // Boolean to determine if modal is in edit mode
  initialData = {}, // Initial data for edit mode (optional, defaults to empty object)
}) => {
  // State to track whether form fields have been touched for validation
  const [touchedFields, setTouchedFields] = useState({
    name: false,
    type: false,
    longitude: false,
    latitude: false,
  });

  // State for form validation errors
  const [errors, setErrors] = useState({
    name: '',
    type: '',
    longitude: '',
    latitude: '',
  });

  // Handle modal open for edit mode and reset validation
  useEffect(() => {
    if (show) {
      setTouchedFields({
        name: false,
        type: false,
        longitude: false,
        latitude: false,
      });
      setErrors({
        name: '',
        type: '',
        longitude: '',
        latitude: '',
      });
      if (isEditMode && initialData) {
        // Auto-fill logic can be implemented here if needed
      }
    }
  }, [show, isEditMode, initialData]); // Dependencies for effect

  /**
   * Handles form input changes with validation for coordinate fields
   * @param {Object} e - The event object
   */
  const handleChange = (e) => {
    const { name, value } = e.target;

    // Validation for coordinate fields
    if (name === 'longitude' || name === 'latitude') {
      // Allow numbers, decimal point, and negative sign (valid coordinate format)
      if (/^-?\d*\.?\d*$/.test(value) || value === '') {
        onChange(e); // Update formData only if valid
        setErrors((prev) => ({ ...prev, [name]: '' }));
      } else {
        setErrors((prev) => ({
          ...prev,
          [name]: `${name.charAt(0).toUpperCase() + name.slice(1)} must contain only numbers, a decimal point, or a negative sign`,
        }));
        return;
      }
    } else {
      // For name and type, pass through without validation
      onChange(e);
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  /**
   * Handles form submission with validation
   * @param {Object} e - The event object
   */
  const handleSubmit = (e) => {
    e.preventDefault();
    // Mark all fields as touched when submitting
    setTouchedFields({
      name: true,
      type: true,
      longitude: true,
      latitude: true,
    });

    // Validate form fields
    const newErrors = {};
    let isValid = true;

    if (!formData.name?.trim()) {
      newErrors.name = 'Name is required';
      isValid = false;
    }
    if (!formData.type?.trim()) {
      newErrors.type = 'Type is required';
      isValid = false;
    }
    if (!formData.latitude?.trim()) {
      newErrors.latitude = 'Latitude is required';
      isValid = false;
    } else if (!/^-?\d*\.?\d*$/.test(formData.latitude)) {
      newErrors.latitude = 'Latitude must contain only numbers, a decimal point, or a negative sign';
      isValid = false;
    }
    if (!formData.longitude?.trim()) {
      newErrors.longitude = 'Longitude is required';
      isValid = false;
    } else if (!/^-?\d*\.?\d*$/.test(formData.longitude)) {
      newErrors.longitude = 'Longitude must contain only numbers, a decimal point, or a negative sign';
      isValid = false;
    }

    setErrors(newErrors);

    // Proceed with save if all fields are valid
    if (isValid) {
      onSave();
    }
  };

  /**
   * Marks a field as touched when it loses focus
   * @param {string} fieldName - The name of the field
   */
  const handleBlur = (fieldName) => {
    if (!touchedFields[fieldName]) {
      setTouchedFields((prev) => ({
        ...prev,
        [fieldName]: true,
      }));
    }
  };

  /**
   * Determines if validation message should be shown for a field
   * @param {string} fieldName - The name of the field to check
   * @returns {boolean} True if validation should be shown
   */
  const shouldShowError = (fieldName) => {
    return touchedFields[fieldName] && (errors[fieldName] || !formData[fieldName]?.toString().trim());
  };

  // Render nothing if modal is not shown
  if (!show) return null;

  // Render modal
  return (
    <div className={`modal fade ${show ? 'show d-block' : ''}`} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          {/* Modal Header */}
          <div className="modal-header">
            <h5 className="modal-title">
              {isEditMode ? 'Edit Medical Center' : 'Add Medical Center'}
            </h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <div className="modal-body">
            <form onSubmit={handleSubmit}>
              <div className="row">
                {/* Name Input */}
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-control ${shouldShowError('name') ? 'is-invalid' : ''}`}
                      name="name"
                      value={formData.name || ''}
                      onChange={handleChange}
                      onBlur={() => handleBlur('name')}
                      placeholder="Name"
                      required
                    />
                    {shouldShowError('name') && (
                      <div className="invalid-feedback">{errors.name || 'Name is required'}</div>
                    )}
                  </div>
                </div>
                {/* Type Selection */}
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      Type <span className="text-danger">*</span>
                    </label>
                    <select
                      className={`form-control form-select ${shouldShowError('type') ? 'is-invalid' : ''}`}
                      name="type"
                      value={formData.type || ''}
                      onChange={handleChange}
                      onBlur={() => handleBlur('type')}
                      required
                    >
                      <option value="">Select Type</option>
                      <option value="Hospital">Hospital</option>
                      <option value="Center">Center</option>
                      <option value="Clinic">Clinic</option>
                    </select>
                    {shouldShowError('type') && (
                      <div className="invalid-feedback">{errors.type || 'Type is required'}</div>
                    )}
                  </div>
                </div>
              </div>

              <div className="row">
                {/* Latitude Input */}
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      Latitude <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-control ${shouldShowError('latitude') ? 'is-invalid' : ''}`}
                      name="latitude"
                      placeholder="Enter latitude (e.g., 12.345 or -12.345)"
                      value={formData.latitude || ''}
                      onChange={handleChange}
                      onBlur={() => handleBlur('latitude')}
                      required
                    />
                    <small className="form-text text-muted">
                      Only numbers, a decimal point, and a negative sign are allowed.
                    </small>
                    {shouldShowError('latitude') && (
                      <div className="invalid-feedback">{errors.latitude || 'Latitude is required'}</div>
                    )}
                  </div>
                </div>
                {/* Longitude Input */}
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      Longitude <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-control ${shouldShowError('longitude') ? 'is-invalid' : ''}`}
                      name="longitude"
                      placeholder="Enter longitude (e.g., 12.345 or -12.345)"
                      value={formData.longitude || ''}
                      onChange={handleChange}
                      onBlur={() => handleBlur('longitude')}
                      required
                    />
                    <small className="form-text text-muted">
                      Only numbers, a decimal point, and a negative sign are allowed.
                    </small>
                    {shouldShowError('longitude') && (
                      <div className="invalid-feedback">{errors.longitude || 'Longitude is required'}</div>
                    )}
                  </div>
                </div>
              </div>
            </form>
          </div>
          {/* Modal Footer */}
          <div className="modal-footer">
            <button type="button" className="btn mb-1 waves-effect waves-light btn-outline-danger" onClick={onClose}>
              Cancel
            </button>
            <button type="button" className="btn mb-1 waves-effect waves-light btn-primary" onClick={handleSubmit}>
              {isEditMode ? 'Update' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Export the component
export default MedicalAddModal;